import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import { getPlanParticipantIds, getPlanParticipantProfiles } from '@components/Plan/Details';
import type { PlanParticipantProfile } from '@components/Plan/Details';
import { PressableOpacity } from '@components/Pressable';
import { useAppTheme } from '@context/ThemeContext';
import type { Plan } from '@types';

const MAX_AVATAR_TILES = 4;

type PlanAvatarProfile = Pick<PlanParticipantProfile, 'displayName' | 'photoUrl'> & {
  id: string;
  userId?: string;
};

type PlanAvatarBubbleItem = PlanAvatarProfile | PlanAvatarMoreItem;

type PlanAvatarMoreItem = {
  displayName: string;
  hiddenCount: number;
  id: string;
  photoUrl: '';
};

export type PlanAvatarFrame = {
  diameter: number;
  left: number;
  top: number;
  zIndex: number;
};

type NormalizedPlanAvatarFrame = PlanAvatarFrame;
type PlanAvatarFrameCount = 1 | 2 | 3 | 4;

type PlanAvatarStackProps = {
  /** Maximum number of avatar tiles shown inside the fixed-size bubble. */
  avatarLimit: number;
  /** Diameter of the outer avatar bubble. */
  avatarSize: number;
  /** Called when an individual user avatar is pressed. */
  onAvatarPress?: (userId: string) => void;
  /** Loaded user profiles for the plan owner and joined users. */
  participantUsers: PlanParticipantProfile[];
  /** Plan whose owner and joined users should be represented. */
  plan: Plan;
};

// Hand-tuned ratios keep 2-4 avatars contained while filling the clipped circle.
const PLAN_AVATAR_FRAME_LAYOUTS: Record<
  PlanAvatarFrameCount,
  readonly NormalizedPlanAvatarFrame[]
> = {
  1: [{ diameter: 1, left: 0, top: 0, zIndex: 1 }],
  2: [
    { diameter: 0.58, left: 0.062, top: 0.062, zIndex: 2 },
    { diameter: 0.4, left: 0.511, top: 0.511, zIndex: 1 },
  ],
  3: [
    { diameter: 0.53, left: 0, top: 0.235, zIndex: 3 },
    { diameter: 0.44, left: 0.49617, top: 0.10204, zIndex: 2 },
    { diameter: 0.40947, left: 0.46174, top: 0.53912, zIndex: 1 },
  ],
  4: [
    { diameter: 0.46, left: 0, top: 0.27, zIndex: 4 },
    { diameter: 0.37, left: 0.37, top: 0.006, zIndex: 3 },
    { diameter: 0.31, left: 0.686, top: 0.395, zIndex: 2 },
    { diameter: 0.25, left: 0.375, top: 0.75, zIndex: 1 },
  ],
};

export default function PlanAvatarStack({
  avatarLimit,
  avatarSize,
  onAvatarPress,
  participantUsers,
  plan,
}: PlanAvatarStackProps) {
  const appTheme = useAppTheme();
  const resolvedAvatarProfiles = getDefaultAvatarProfiles(plan, participantUsers);
  const totalAvatarCount = getPlanParticipantIds(plan).length;
  const displayedAvatars = getDisplayedAvatarItems(
    resolvedAvatarProfiles,
    totalAvatarCount,
    avatarLimit
  );
  const avatarFrames = getPlanAvatarFrames(displayedAvatars.length, avatarSize);
  const avatarContainerStyle = {
    backgroundColor: appTheme.colors.disabled,
    borderRadius: avatarSize / 2,
    height: avatarSize,
    width: avatarSize,
  };
  const moreAvatarTextStyle = {
    color: appTheme.colors.surface,
  };
  const separatorColor = appTheme.colors.surface;
  const moreAvatarStyle = {
    backgroundColor: appTheme.colors.accent,
  };

  return (
    <View
      accessibilityLabel={`${totalAvatarCount} ${totalAvatarCount === 1 ? 'person' : 'people'}`}
      accessibilityRole="image"
      style={[styles.avatarStack, avatarContainerStyle]}
      testID="components.PlanSummary.AvatarStack">
      {displayedAvatars.map((profile, index) => {
        const frame = avatarFrames[index];
        if (!frame) return null;

        const userId = 'hiddenCount' in profile ? undefined : profile.userId;
        const onPress = userId && onAvatarPress ? () => onAvatarPress(userId) : undefined;
        const frameStyle = getAvatarFrameStyle(frame, avatarSize, separatorColor);

        return (
          <PressableOpacity
            key={profile.id}
            disabled={!onPress}
            onPress={onPress}
            style={[styles.avatarBubble, frameStyle, 'hiddenCount' in profile && moreAvatarStyle]}
            testID={
              'hiddenCount' in profile
                ? 'components.PlanSummary.AvatarMoreBadge'
                : `components.PlanSummary.Avatar.${profile.id}`
            }>
            {'hiddenCount' in profile ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.moreBadgeText,
                  moreAvatarTextStyle,
                  { fontSize: Math.max(10, Math.round(frame.diameter * 0.3)) },
                ]}>
                +{profile.hiddenCount}
              </Text>
            ) : (
              <UserAvatar
                displayName={profile.displayName}
                photoUrl={profile.photoUrl}
                size={frame.diameter}
                style={styles.avatarTile}
              />
            )}
          </PressableOpacity>
        );
      })}
    </View>
  );
}

export function getPlanAvatarFrames(count: number, size: number): PlanAvatarFrame[] {
  return PLAN_AVATAR_FRAME_LAYOUTS[getPlanAvatarFrameCount(count)].map((frame) => ({
    diameter: frame.diameter * size,
    left: frame.left * size,
    top: frame.top * size,
    zIndex: frame.zIndex,
  }));
}

function getDefaultAvatarProfiles(
  plan: Plan,
  participantUsers: PlanParticipantProfile[]
): PlanAvatarProfile[] {
  return getPlanParticipantProfiles(plan, participantUsers).map((user) => ({
    displayName: user.displayName,
    id: user.userId,
    photoUrl: user.photoUrl,
    userId: user.userId,
  }));
}

function getDisplayedAvatarItems(
  avatarProfiles: PlanAvatarProfile[],
  totalAvatarCount: number,
  avatarLimit: number
): PlanAvatarBubbleItem[] {
  const maxAvatarCount = Math.max(1, Math.min(avatarLimit, MAX_AVATAR_TILES));
  let visibleAvatarProfiles = avatarProfiles.slice(0, maxAvatarCount);
  let hiddenAvatarCount = Math.max(totalAvatarCount - visibleAvatarProfiles.length, 0);

  if (hiddenAvatarCount > 0 && visibleAvatarProfiles.length >= maxAvatarCount) {
    visibleAvatarProfiles = avatarProfiles.slice(0, Math.max(maxAvatarCount - 1, 1));
    hiddenAvatarCount = Math.max(totalAvatarCount - visibleAvatarProfiles.length, 0);
  }

  if (hiddenAvatarCount === 0) return visibleAvatarProfiles;

  return [
    ...visibleAvatarProfiles,
    {
      displayName: `${hiddenAvatarCount} more`,
      hiddenCount: hiddenAvatarCount,
      id: 'remaining-count',
      photoUrl: '',
    },
  ];
}

function getPlanAvatarFrameCount(count: number): PlanAvatarFrameCount {
  if (count <= 1) return 1;
  if (count === 2) return 2;
  if (count === 3) return 3;
  return 4;
}

function getAvatarFrameStyle(frame: PlanAvatarFrame, parentSize: number, separatorColor: string) {
  return {
    borderColor: separatorColor,
    borderRadius: frame.diameter / 2,
    borderWidth: Math.max(1.5, parentSize * 0.025),
    height: frame.diameter,
    left: frame.left,
    top: frame.top,
    width: frame.diameter,
    zIndex: frame.zIndex,
  };
}

const styles = StyleSheet.create({
  avatarBubble: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'absolute',
  },
  avatarStack: {
    flexShrink: 0,
    marginHorizontal: 5,
    marginLeft: 10,
    overflow: 'hidden',
    position: 'relative',
  },
  avatarTile: {
    borderWidth: 0,
  },
  moreBadgeText: {
    fontVariant: ['tabular-nums'],
    fontWeight: '700',
    textAlign: 'center',
  },
});
